<script setup>
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import IconOwnerReport from "@/components/icons/IconOwnerReport.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import DepartmentService from "@/api/services/department.service";
import TrackerService from "@/api/services/tracker.service";
import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";

const authStore = useAuthStore();
const uiStore = useUiStore();
const user = computed(() => authStore.getUserLogged);

const departments = ref([]);
const selectedDepartment = ref(null);
const selectedDatePreset = ref("last_month");
const selectedDateRange = ref([]);
const rows = ref([]);
const totals = ref({ hours: 0, amount: 0 });
const page = ref(1);
const itemsPerPage = ref(50);
const totalItems = ref(0);
const hourlyCostsFileInput = ref(null);

const datePresetOptions = [
  { label: "Hoy", value: "today" },
  { label: "Ayer", value: "yesterday" },
  { label: "Esta semana", value: "this_week" },
  { label: "Semana pasada", value: "last_week" },
  { label: "Este mes", value: "this_month" },
  { label: "El mes pasado", value: "last_month" },
  { label: "Personalizado", value: "custom" },
];
const datePickerOptions = {
  firstDayOfWeek: 1,
};
const datePickerShortcuts = [
  { text: "Hoy", value: () => getPresetRange("today") },
  { text: "Ayer", value: () => getPresetRange("yesterday") },
  { text: "Esta semana", value: () => getPresetRange("this_week") },
  { text: "Semana pasada", value: () => getPresetRange("last_week") },
  { text: "Este mes", value: () => getPresetRange("this_month") },
  { text: "El mes pasado", value: () => getPresetRange("last_month") },
];

const canSearch = computed(() =>
  !!selectedDepartment.value && Array.isArray(selectedDateRange.value) && selectedDateRange.value.length === 2
);

onMounted(async () => {
  applyDatePreset(selectedDatePreset.value);
  await loadDepartments();
});

const loadDepartments = async () => {
  uiStore.startLoading();
  DepartmentService.getDepartments({
    itemsPerPage: 9999,
    "order[name]": "ASC",
  })
    .then((response) => {
      departments.value = response.data["hydra:member"];
      selectedDepartment.value = departments.value.find((department) => department.name === "Desarrollo") ?? null;
      uiStore.stopLoading();
      search(true);
    })
    .catch((error) => {
      console.log(error);
      notification.addNotification({
        message: "Error al cargar los departamentos",
        type: "error",
        duration: 6000,
      });
      uiStore.stopLoading();
    });
};

const search = (resetPage = false) => {
  if (resetPage) {
    page.value = 1;
  }

  if (!canSearch.value) {
    rows.value = [];
    totals.value = { hours: 0, amount: 0 };
    totalItems.value = 0;
    return;
  }

  uiStore.startLoading();
  TrackerService.getOwnerReport(buildParams())
    .then((response) => {
      rows.value = response.data["hydra:member"];
      totals.value = response.data.totals ?? { hours: 0, amount: 0 };
      totalItems.value = response.data["hydra:totalItems"] ?? 0;
    })
    .catch((error) => {
      console.log(error);
      notification.addNotification({
        message: "Error al cargar el informe propietario",
        type: "error",
        duration: 6000,
      });
    })
    .finally(() => {
      uiStore.stopLoading();
    });
};

const downloadReport = () => {
  if (!canSearch.value) return;

  hourlyCostsFileInput.value?.click();
};

const handleHourlyCostsFileSelected = (event) => {
  const file = event.target.files?.[0] ?? null;
  event.target.value = "";

  if (!file) return;

  if (!isValidHourlyCostsFile(file)) {
    notification.addNotification({
      message: "Selecciona un fichero Excel .xls o .xlsx con los costes por hora",
      type: "error",
      duration: 6000,
    });
    return;
  }

  uiStore.startLoading();
  TrackerService.getOwnerReportExport(buildParams(), file)
    .then((response) => {
      handleFileDownload(response);
      notification.addNotification({
        message: "Informe descargado correctamente",
        type: "success",
        duration: 6000,
      });
    })
    .catch(async (error) => {
      console.log(error);
      notification.addNotification({
        message: await getDownloadErrorMessage(error),
        type: "error",
        duration: 6000,
      });
    })
    .finally(() => {
      uiStore.stopLoading();
    });
};

const isValidHourlyCostsFile = (file) => /\.(xlsx|xls)$/i.test(file.name);

const getDownloadErrorMessage = async (error) => {
  const fallbackMessage = "Error al descargar el informe propietario";
  const responseData = error?.response?.data;

  if (responseData instanceof Blob) {
    const text = await responseData.text();
    if (!text) return fallbackMessage;

    try {
      const parsed = JSON.parse(text);
      return parsed.detail ?? parsed.message ?? fallbackMessage;
    } catch {
      return text;
    }
  }

  return responseData?.detail ?? responseData?.message ?? fallbackMessage;
};

const buildParams = () => ({
  departmentId: selectedDepartment.value.id,
  timeStart: formatDate(startOfDay(selectedDateRange.value[0])),
  timeEnd: formatDate(endOfDay(selectedDateRange.value[1])),
  page: page.value,
  itemsPerPage: itemsPerPage.value,
});

const handleDatePresetChange = (preset) => {
  applyDatePreset(preset);
  search(true);
};

const handleDateRangeChange = (range) => {
  if (!range || range.length !== 2) {
    selectedDatePreset.value = "last_month";
    applyDatePreset(selectedDatePreset.value);
  } else {
    selectedDatePreset.value = detectPresetFromRange(range) ?? "custom";
  }
  search(true);
};

const changePagination = () => {
  search();
};

const applyDatePreset = (preset) => {
  selectedDateRange.value = getPresetRange(preset);
};

const getPresetRange = (preset) => {
  const now = new Date();

  if (preset === "yesterday") {
    const yesterday = addDays(startOfDay(now), -1);
    return [yesterday, endOfDay(yesterday)];
  }

  if (preset === "this_week") {
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const start = addDays(startOfDay(now), diffToMonday);
    return [start, endOfDay(now)];
  }

  if (preset === "last_week") {
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const startOfThisWeek = addDays(startOfDay(now), diffToMonday);
    const start = addDays(startOfThisWeek, -7);
    const end = addDays(start, 6);
    return [start, endOfDay(end)];
  }

  if (preset === "this_month") {
    return [new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0), endOfDay(now)];
  }

  if (preset === "last_month") {
    return [
      new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0),
      new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999),
    ];
  }

  if (preset === "custom") {
    return selectedDateRange.value?.length === 2 ? selectedDateRange.value : [startOfDay(now), endOfDay(now)];
  }

  return [startOfDay(now), endOfDay(now)];
};

const detectPresetFromRange = (range) => {
  const normalizedRange = normalizeRange(range);
  return datePresetOptions
    .filter((option) => option.value !== "custom")
    .find((option) => compareRanges(normalizedRange, normalizeRange(getPresetRange(option.value))))
    ?.value ?? null;
};

const normalizeRange = (range) => [
  formatDate(startOfDay(range[0])),
  formatDate(endOfDay(range[1])),
];

const compareRanges = (firstRange, secondRange) => firstRange[0] === secondRange[0] && firstRange[1] === secondRange[1];

const startOfDay = (date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
};

const endOfDay = (date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(23, 59, 59, 999);
  return normalizedDate;
};

const addDays = (date, days) => {
  const normalizedDate = new Date(date);
  normalizedDate.setDate(normalizedDate.getDate() + days);
  return normalizedDate;
};

const formatDate = (date) => {
  const offset = new Date(date).getTimezoneOffset();
  const sign = offset > 0 ? "-" : "+";
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
  const minutes = String(Math.abs(offset) % 60).padStart(2, "0");
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hoursOfDay = String(date.getHours()).padStart(2, "0");
  const minutesOfDay = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hoursOfDay}:${minutesOfDay}:${seconds}${sign}${hours}:${minutes}`;
};

const formatNumber = (value) => new Intl.NumberFormat("es-ES", {
  useGrouping: "always",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(value || 0);

const formatCurrency = (value) => new Intl.NumberFormat("es-ES", {
  useGrouping: "always",
  style: "currency",
  currency: "EUR",
}).format(value || 0);

const handleFileDownload = (response) => {
  const blob = new Blob([response.data], { type: response.headers["content-type"] });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", getFileName(response));
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getFileName = (response) => {
  const contentDisposition = response.headers["content-disposition"];
  if (contentDisposition) {
    const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = fileNameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      return matches[1].replace(/['"]/g, "");
    }
  }

  return "Informe Propietario.xlsx";
};
</script>

<template>
  <div v-if="user && (user.roles.includes('ROLE_ADMIN') || user.accessTrackerNetReports)" class="owner-report-body">
    <HeaderTitle title="Administración" />
    <div class="d-flex flex-row justify-content-start owner-report-header">
      <IconOwnerReport class="owner-report-title-icon" />
      <div class="title-text ml-4">
        Administración &middot; Informe propietario
      </div>
    </div>

    <el-row class="mt-2">
      <el-card class="card-search w-100">
        <div v-if="selectedDatePreset !== 'custom'" class="owner-report-visual-background">
          <img src="@/assets/images/owner-report-billing-visual.png" alt="" class="owner-report-visual" />
        </div>
        <div class="row owner-report-filters-row">
          <div class="col-12 col-md owner-report-filter-column">
            <div class="form-item mb-1">
              <label class="label-state">Departamento:</label>
              <el-select class="w-100" v-model="selectedDepartment" value-key="@id" filterable placeholder="Departamento"
                @change="search(true)">
                <el-option v-for="department in departments" :key="department['@id']" :label="department.name"
                  :value="department" />
              </el-select>
            </div>
          </div>
          <div class="col-12 col-md owner-report-filter-column">
            <div class="form-item mb-1">
              <label class="label-state">Periodo:</label>
              <el-select class="w-100" v-model="selectedDatePreset" @change="handleDatePresetChange">
                <el-option v-for="option in datePresetOptions" :key="option.value" :label="option.label"
                  :value="option.value" />
              </el-select>
            </div>
          </div>
          <div v-if="selectedDatePreset === 'custom'" class="col-12 col-md owner-report-dates-column">
            <div class="form-item date-picker mb-1 owner-report-date-field">
              <label>*Fechas:</label>
              <el-date-picker v-model="selectedDateRange" type="daterange" start-placeholder="Fecha inicio"
                end-placeholder="Fecha fin" range-separator="-" unlink-panels format="DD/MM/YYYY"
                class="w-100 owner-report-date-range" :picker-options="datePickerOptions"
                :shortcuts="datePickerShortcuts" @change="handleDateRangeChange" />
            </div>
          </div>
          <div class="col-12 col-md-auto owner-report-actions-column">
            <button class="btn btn_red btn-search owner-report-search-button" :disabled="!canSearch" @click="search(true)">
              BUSCAR
            </button>
            <button class="btn btn_yellow btn-search owner-report-download-button" :disabled="!canSearch"
              @click="downloadReport">
              DESCARGAR INFORME
            </button>
            <input ref="hourlyCostsFileInput" type="file" accept=".xls,.xlsx" class="d-none"
              @change="handleHourlyCostsFileSelected" />
          </div>
        </div>
      </el-card>
    </el-row>

    <el-card v-if="totalItems > 0" class="card-tracker owner-report-results-card mt-3">
      <template #header>
        <div class="card-tracker-header owner-report-results-header">
          <div class="row ms-0 owner-report-totals">
            <div class="col-3 ms-0 el-col-xs-24">
              Total:
              <span class="inputHour">{{ totalItems }}</span>
            </div>
            <div class="col-3 ms-0 el-col-xs-24">
              Horas:
              <span class="inputHour">{{ formatNumber(totals.hours) }}</span>
            </div>
            <div class="col-4 ms-0 el-col-xs-24">
              Importe:
              <span class="inputHour inputNotTotal">{{ formatCurrency(totals.amount) }}</span>
            </div>
          </div>
        </div>
      </template>
      <template #default>
        <div v-if="rows.length > 0" class="table table-not-mobile owner-report-table">
          <table>
            <thead>
              <tr>
                <th>Propietario</th>
                <th>Nombre de proyecto</th>
                <th>Nombre empleado</th>
                <th>Cliente</th>
                <th>Nº Horas</th>
                <th>CosteHora</th>
                <th>Importe</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="`${row.ownerDepartmentId}-${row.projectId}-${row.employeeId}`">
                <td>{{ row.ownerDepartmentName }}</td>
                <td>{{ row.projectName }}</td>
                <td>{{ row.employeeName }}</td>
                <td>{{ row.clientName }}</td>
                <td class="text-end">{{ formatNumber(row.hours) }}</td>
                <td class="text-end">{{ formatCurrency(row.hourlyCost) }}</td>
                <td class="text-end">{{ formatCurrency(row.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="rows.length > 0" class="table-mobile owner-report-mobile-list">
          <el-card v-for="row in rows" :key="`mobile-${row.ownerDepartmentId}-${row.projectId}-${row.employeeId}`"
            class="owner-report-mobile-card">
            <div class="owner-report-mobile-title">{{ row.projectName }}</div>
            <div class="owner-report-mobile-line"><span>Propietario</span><strong>{{ row.ownerDepartmentName }}</strong></div>
            <div class="owner-report-mobile-line"><span>Empleado</span><strong>{{ row.employeeName }}</strong></div>
            <div class="owner-report-mobile-line"><span>Cliente</span><strong>{{ row.clientName }}</strong></div>
            <div class="owner-report-mobile-line"><span>Horas</span><strong>{{ formatNumber(row.hours) }}</strong></div>
            <div class="owner-report-mobile-line"><span>CosteHora</span><strong>{{ formatCurrency(row.hourlyCost) }}</strong></div>
            <div class="owner-report-mobile-line owner-report-mobile-total"><span>Importe</span><strong>{{ formatCurrency(row.amount) }}</strong></div>
          </el-card>
        </div>
      </template>
    </el-card>

    <div v-if="totalItems > 0" class="mt-4">
      <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage" size="default"
        :page-size-options="[25, 50, 100, 200]" layout="prev, pager, next, sizes" :total="totalItems"
        class="owner-report-pagination" show-always @change="changePagination" />
    </div>

    <el-empty v-if="canSearch && rows.length === 0" description="Sin datos para los filtros seleccionados" />
  </div>
  <div v-else>No tienes permiso para ver esta sección</div>
</template>

<style scoped lang="scss">
.owner-report-body {
  .owner-report-header {
    align-items: center;

    .owner-report-title-icon {
      color: #474ce6;
      height: 34px;
      width: 34px;
    }
  }

  .card-search {
    background: white;
    border-radius: 15px;
    height: auto;
    overflow: hidden !important;
    position: relative;
  }

  .owner-report-filters-row {
    align-items: flex-end;
    position: relative;
    row-gap: 12px;
    z-index: 2;
  }

  .owner-report-filter-column {
    flex: 1 1 240px;
    min-width: 0;
  }

  .owner-report-dates-column {
    flex: 1 1 320px;
    min-width: 0;
  }

  .owner-report-filters-row .form-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 0 !important;
  }

  :deep(.owner-report-date-range.el-input__wrapper) {
    border-radius: 20px;
    height: 44px;
    min-height: 44px;
    min-width: 0;
    width: 100%;
    overflow: hidden;
  }


  .owner-report-actions-column {
    align-items: flex-end;
    display: flex;
    flex: 1 1 440px;
    flex-wrap: nowrap;
    gap: 12px;
    justify-content: flex-end;
    margin-left: auto;
    min-width: 0;
  }

  .owner-report-visual-background {
    bottom: 0;
    display: none;
    overflow: hidden;
    position: absolute;
    right: 0;
    top: 0;
    width: 58%;
    z-index: 0;
  }

  .owner-report-visual-background::before {
    background: linear-gradient(90deg, #fff 0%, rgba(255, 255, 255, 0.96) 16%, rgba(255, 255, 255, 0.68) 42%, rgba(255, 255, 255, 0.16) 76%, rgba(255, 255, 255, 0) 100%);
    content: "";
    inset: 0;
    pointer-events: none;
    position: absolute;
    z-index: 1;
  }

  .owner-report-visual {
    height: 100%;
    inset: 0;
    object-fit: cover;
    object-position: right center;
    opacity: 0.96;
    pointer-events: none;
    position: absolute;
    transform: scale(1.08);
    transform-origin: right center;
    width: 100%;
  }

  @media (min-width: 768px) {
    .owner-report-visual-background {
      display: block;
    }
  }

  .owner-report-search-button,
  .owner-report-download-button {
    align-items: center;
    display: inline-flex;
    justify-content: center;
    margin-bottom: 0 !important;
    margin-right: 0 !important;
    flex: 0 1 200px;
    min-height: 44px;
    min-width: 0;
    padding: 10px 16px;
    white-space: normal;
    width: auto;
  }

  .owner-report-search-button {
    color: #fff !important;
  }

  .owner-report-search-button:hover {
    color: #fff !important;
  }

  .owner-report-download-button {
    color: #474ce6 !important;
  }

  .owner-report-search-button:disabled,
  .owner-report-download-button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .owner-report-results-card {
    border-radius: 15px !important;
    font-family: Montserrat;
    overflow: hidden;

    :deep(.el-card__header) {
      border-top-left-radius: 15px !important;
      border-top-right-radius: 15px !important;
      padding: 0 !important;
    }

    :deep(.el-card__body) {
      padding-bottom: 0;
      padding-top: 0;
    }
  }

  .owner-report-results-header {
    min-height: 38px;
    padding-bottom: 2px;
    padding-top: 2px;
  }

  .owner-report-totals {
    min-width: 720px;
    width: 100%;

    > div {
      align-items: baseline;
      color: #474ce6;
      display: flex;
      gap: 6px;
      min-height: 30px;
    }
  }

  .inputHour {
    color: #000;
    font-size: 24px;
    font-weight: 500;
    line-height: 1.2;
  }

  .inputNotTotal {
    font-weight: 500;
  }

  .owner-report-table {
    margin-bottom: 0;
    padding: 0 24px;

    table {
      border-collapse: collapse;
      width: 100%;
    }

    thead {
      border-bottom: 1px solid #d9d9e3;
    }

    th,
    td {
      color: #34364a;
      font-size: 14px;
      font-weight: 500;
      padding: 12px 10px;
      vertical-align: middle;
    }

    th {
      color: #474ce6;
      font-size: 15px;
      font-weight: 500;
    }

    tbody tr {
      border-bottom: 1px solid #e1e1e8;
    }

    tbody tr:last-child {
      border-bottom: none;
    }
  }

  .table-mobile {
    display: none;
  }

  .owner-report-mobile-card {
    border-radius: 8px;
    margin-bottom: 12px;
  }

  .owner-report-mobile-title {
    color: #474ce6;
    font-weight: 700;
    margin-bottom: 10px;
  }

  .owner-report-mobile-line {
    align-items: flex-start;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 4px 0;

    span {
      color: #6f7085;
      flex: 0 0 92px;
    }

    strong {
      min-width: 0;
      overflow-wrap: anywhere;
      text-align: right;
    }
  }

  .owner-report-mobile-total {
    border-top: 1px solid #e9e9f4;
    margin-top: 6px;
    padding-top: 10px;
  }
}

@media (max-width: 767px) {
  .owner-report-body {
    #header-title {
      height: 75px !important;
    }

    .owner-report-header {
      .title-text {
        font-size: 24px !important;
      }
    }

    .owner-report-filter-column,
    .owner-report-dates-column,
    .owner-report-actions-column {
      flex: 0 0 100%;
      max-width: 100%;
      width: 100%;
    }

    .owner-report-actions-column {
      flex-direction: column;
      align-items: stretch;
    }

    .owner-report-search-button,
    .owner-report-download-button {
      flex: none;
      width: 100%;
    }

    .owner-report-results-header {
      align-items: flex-start;
      padding: 8px 12px !important;
    }

    .owner-report-totals {
      min-width: 100px;
      row-gap: 4px;

      > div {
        min-height: 30px;
      }
    }

    .inputHour {
      font-size: 20px;
    }

    .table-not-mobile {
      display: none;
    }

    .table-mobile {
      display: block;
    }
  }
}
</style>
